/**
 * Utility functions for datetime conversions and formatting
 * Used across filter components and form inputs
 */

/**
 * Convert datetime string (YYYY-MM-DDTHH:mm:ss or ISO format) to timestamp for NDatePicker
 * @param datetimeStr - Datetime string in ISO format or YYYY-MM-DDTHH:mm:ss
 * @returns Timestamp in milliseconds or null if invalid
 */
export function datetimeStringToTimestamp(
  datetimeStr: string | number | null | undefined
): number | null {
  if (!datetimeStr) return null;
  const date = new Date(String(datetimeStr));
  if (isNaN(date.getTime())) return null;
  return date.getTime();
}

/**
 * Convert timestamp from NDatePicker to ISO datetime string
 * @param timestamp - Timestamp in milliseconds
 * @returns ISO format string (YYYY-MM-DDTHH:mm:ss) or empty string if invalid
 */
export function timestampToDatetimeString(timestamp: number | null): string {
  if (timestamp === null) return '';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  // Return ISO format: YYYY-MM-DDTHH:mm:ss
  return date.toISOString().slice(0, 19);
}

/**
 * Check if a value is a datetime string (ISO format)
 * @param value - Value to check
 * @returns True if value is a datetime string in ISO format
 */
export function isDatetimeString(value: string | number | null | undefined): boolean {
  if (!value || typeof value !== 'string') return false;
  // Check for ISO datetime format: YYYY-MM-DDTHH:mm:ss or YYYY-MM-DD HH:mm:ss
  const datetimePattern = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/;
  return datetimePattern.test(value);
}

/**
 * Format datetime string for table/list display (locale with date and time).
 * @param dateString - ISO or datetime string, or null/undefined
 * @returns Formatted string (e.g. "Feb 3, 2025, 02:30 PM") or "—" if null/undefined/invalid
 */
export function formatDateTimeForTable(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format datetime string for date-only display (no time).
 * @param dateString - ISO or datetime string, or null/undefined
 * @returns Formatted string (e.g. "Feb 3, 2025") or "—" if null/undefined/invalid
 */
export function formatDateForDisplay(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a duration for compact display.
 * @param seconds - Duration in seconds
 * @returns e.g. "42s", "12m", "3h 20m", "2d 4h", or "—" for null/undefined/negative
 */
export function formatDurationFromSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return '—';
  }
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(seconds / 3600);
  if (hours < 24) {
    const restMinutes = Math.round((seconds - hours * 3600) / 60);
    return restMinutes > 0 ? `${hours}h ${restMinutes}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours > 0 ? `${days}d ${restHours}h` : `${days}d`;
}

/**
 * Format datetime string for display
 * @param datetimeStr - Datetime string to format
 * @returns Formatted string (YYYY-MM-DD HH:mm:ss) or original string if invalid
 */
export function formatDatetimeForDisplay(datetimeStr: string): string {
  try {
    const date = new Date(datetimeStr);
    if (isNaN(date.getTime())) return datetimeStr;
    // Format as: YYYY-MM-DD HH:mm:ss
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch {
    return datetimeStr;
  }
}
